"use client";

import {
  getKioskIdentity,
  getShortKioskId,
  setKioskLocation,
  setKioskName,
} from "@/app/directories/kiosk/lib/kiosk-identity";
import {
  fetchServerVersion,
  getBuildReleasedAt,
  getBuildVersion,
} from "@/lib/app-version";
import { getPracticesByOrgId } from "@frontend/lib/actions/actions";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

// localStorage helper for kiosk settings persistence
const SETTINGS_PREFIX = "kiosk_settings_";

interface KioskSettings {
  machineContext?: {
    orgId: number;
    practiceId: string;
    orgName: string;
    qrMode: boolean;
    ehrLocationId?: string;
  };
  inactivityTimeout?: number;
}

/**
 * Check if localStorage is available
 */
const isStorageAvailable = (): boolean => {
  try {
    const test = "__storage_test__";
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch {
    return false;
  }
};

/**
 * Save a setting to localStorage
 */
const saveToLocalStorage = (key: string, value: unknown): void => {
  if (!isStorageAvailable()) {
    console.warn(
      `[SettingsTab] localStorage unavailable, ${key} not persisted`,
    );
    return;
  }

  try {
    const storageKey = `${SETTINGS_PREFIX}${key}`;
    localStorage.setItem(storageKey, JSON.stringify(value));
    console.log(`[SettingsTab] Saved ${key} to localStorage`);
  } catch (error) {
    console.error(`[SettingsTab] Failed to save ${key}:`, error);
  }
};

/**
 * Load a setting from localStorage
 */
const loadFromLocalStorage = <T,>(key: string): T | null => {
  if (!isStorageAvailable()) {
    return null;
  }

  try {
    const storageKey = `${SETTINGS_PREFIX}${key}`;
    const json = localStorage.getItem(storageKey);
    if (!json) return null;

    const value = JSON.parse(json) as T;
    console.log(`[SettingsTab] Loaded ${key} from localStorage`);
    return value;
  } catch (error) {
    console.error(`[SettingsTab] Failed to load ${key}:`, error);
    return null;
  }
};

interface SettingsTabProps {
  context: Record<string, unknown>;
  onContextChange?: (updates: Record<string, unknown>) => void;
  inactivityTimeout: number;
  onInactivityTimeoutChange: (timeout: number) => void;
  stripeReaderId?: string | null;
  onStripeReaderIdChange?: (readerId: string | null) => void;
}

export function SettingsTab({
  context,
  onContextChange,
  inactivityTimeout,
  onInactivityTimeoutChange,
  stripeReaderId,
  onStripeReaderIdChange,
}: SettingsTabProps) {
  // Version and identity state
  const [kioskIdentity, setKioskIdentity] = useState(getKioskIdentity());
  const [serverVersion, setServerVersion] = useState<string | null>(null);
  const [isCheckingVersion, setIsCheckingVersion] = useState(false);
  const [localKioskName, setLocalKioskName] = useState(
    kioskIdentity.kioskName || "",
  );
  const [localKioskLocation, setLocalKioskLocation] = useState(
    kioskIdentity.kioskLocation || "",
  );

  const buildVersion = getBuildVersion();
  const buildReleasedAt = getBuildReleasedAt();

  // Check for server version on mount
  useEffect(() => {
    const checkVersion = async () => {
      setIsCheckingVersion(true);
      const info = await fetchServerVersion();
      if (info) {
        setServerVersion(info.version);
      }
      setIsCheckingVersion(false);
    };
    checkVersion();
  }, []);

  const handleSaveIdentity = () => {
    if (localKioskName) {
      setKioskName(localKioskName);
    }
    if (localKioskLocation) {
      setKioskLocation(localKioskLocation);
    }
    setKioskIdentity(getKioskIdentity());
  };

  const [localOrgId, setLocalOrgId] = useState(String(context.orgId || 4));
  const [localPracticeId, setLocalPracticeId] = useState(
    String(context.practiceId || "8"),
  );
  const [localEhrLocationId, setLocalEhrLocationId] = useState(
    String(context.ehrLocationId || ""),
  );
  const [localOrgName, setLocalOrgName] = useState(
    String(context.orgName || "Testerscript"),
  );
  const [localQrMode, setLocalQrMode] = useState(
    Boolean(context.qrMode || false),
  );

  const { data: practiceData } = useQuery({
    queryKey: ["practiceData", localOrgId],
    queryFn: async () => {
      const practiceData = await getPracticesByOrgId(Number(localOrgId));
      return practiceData;
    },
  });

  // Behavior settings
  const [navigationMode, setNavigationMode] = useState(
    String(context.navigationMode || "window"),
  );
  const [permissionRequestMode, setPermissionRequestMode] = useState(
    String(context.permissionRequestMode || "startup"),
  );
  // Load persisted settings from localStorage on mount (only once per session)
  // biome-ignore lint/correctness/useExhaustiveDependencies: Only load once per session
  useEffect(() => {
    // Check if we've already loaded settings this session
    const hasLoadedSettings = sessionStorage.getItem("kiosk_settings_loaded");

    if (hasLoadedSettings) {
      // Just update local state from context, don't reload from localStorage
      setLocalOrgId(String(context.orgId || 4));
      setLocalPracticeId(String(context.practiceId || "8"));
      setLocalEhrLocationId(String(context.ehrLocationId || ""));
      setLocalOrgName(String(context.orgName || "Testerscript"));
      setLocalQrMode(Boolean(context.qrMode || false));
      return;
    }

    const loadSettings = () => {
      try {
        const machineContext = loadFromLocalStorage<{
          orgId: number;
          practiceId: string;
          orgName: string;
          qrMode: boolean;
          ehrLocationId?: string;
        }>("machineContext");

        const timeout = loadFromLocalStorage<number>("inactivityTimeout");

        if (machineContext) {
          setLocalOrgId(String(machineContext.orgId));
          setLocalPracticeId(String(machineContext.practiceId));
          setLocalEhrLocationId(String(machineContext.ehrLocationId || ""));
          setLocalOrgName(machineContext.orgName);
          setLocalQrMode(machineContext.qrMode);

          // Apply to machine context only on first load
          if (onContextChange) {
            onContextChange(machineContext);
          }
        }

        if (timeout !== null && timeout !== undefined) {
          onInactivityTimeoutChange(timeout);
        }

        // Mark as loaded for this session to prevent re-loading on tab switches
        sessionStorage.setItem("kiosk_settings_loaded", "true");
      } catch (error) {
        console.error("Failed to load settings from localStorage:", error);
      }
    };

    loadSettings();
  }, []); // Only run on first mount of the session

  const presetOptions = [
    { label: "5 seconds", value: 5000 },
    { label: "30 seconds", value: 30000 },
    { label: "1 minute (default)", value: 60000 },
    { label: "2 minutes", value: 120000 },
    { label: "5 minutes ", value: 300000 },
    { label: "10 minutes", value: 600000 },
  ];

  const formatTime = (ms: number) => {
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return remainingSeconds > 0
      ? `${minutes}m ${remainingSeconds}s`
      : `${minutes}m`;
  };

  const handleApplyMachineContext = () => {
    const newContext = {
      orgId: Number(localOrgId),
      practiceId: localPracticeId,
      ehrLocationId: localEhrLocationId || undefined,
      orgName: localOrgName,
      qrMode: localQrMode,
    };

    // Save to localStorage
    saveToLocalStorage("machineContext", newContext);

    // Apply to machine
    if (onContextChange) {
      onContextChange(newContext);
    }
  };

  const handleTimeoutChange = (timeout: number) => {
    // Save to localStorage (using settings prefix)
    saveToLocalStorage("inactivityTimeout", timeout);

    // Also save with legacy key for backward compatibility
    try {
      if (typeof window !== "undefined") {
        localStorage.setItem("kiosk_inactivity_timeout", String(timeout));
        console.log(
          "Inactivity timeout persisted to localStorage (legacy key)",
        );
      }
    } catch (error) {
      console.error("Failed to save timeout to localStorage:", error);
    }

    // Apply to machine
    onInactivityTimeoutChange(timeout);
  };

  return (
    <div className="space-y-6 p-4">
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-2">
          Kiosk Settings
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          Configure kiosk behavior and machine context
        </p>
      </div>

      {/* Version & Identity */}
      <div className="space-y-3 pb-6 border-b border-gray-200">
        <div>
          <h4 className="text-sm font-medium text-gray-700">
            Version & Identity
          </h4>
          <p className="text-xs text-gray-500 mt-1">
            Kiosk version info and device identification
          </p>
        </div>

        {/* Version Info */}
        <div className="bg-gray-50 rounded-lg p-3 space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-xs font-medium text-gray-600">
              Build Version
            </span>
            <span className="text-sm font-mono font-bold text-gray-900">
              {buildVersion}
            </span>
          </div>
          {buildReleasedAt && (
            <div className="flex justify-between items-center">
              <span className="text-xs font-medium text-gray-600">
                Released
              </span>
              <span className="text-xs text-gray-700">
                {new Date(buildReleasedAt).toLocaleString()}
              </span>
            </div>
          )}
          <div className="flex justify-between items-center">
            <span className="text-xs font-medium text-gray-600">
              Server Version
            </span>
            {isCheckingVersion ? (
              <span className="text-xs text-gray-500">Checking...</span>
            ) : serverVersion ? (
              <span className={"text-sm font-mono font-bold"}>
                {serverVersion}
              </span>
            ) : (
              <span className="text-xs text-gray-500">Unknown</span>
            )}
          </div>
        </div>

        {/* Kiosk Identity */}
        <div className="bg-blue-50 rounded-lg p-3 space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-xs font-medium text-blue-700">Kiosk ID</span>
            <span className="text-xs font-mono text-blue-900">
              {getShortKioskId()}...
            </span>
          </div>
          <div className="text-xs text-blue-600 break-all">
            Full: {kioskIdentity.kioskId}
          </div>
        </div>

        {/* Editable Name & Location */}
        <div className="space-y-2">
          <div>
            <label
              htmlFor="kiosk-name-input"
              className="text-xs font-medium text-gray-600"
            >
              Kiosk Name (optional)
            </label>
            <input
              id="kiosk-name-input"
              type="text"
              value={localKioskName}
              onChange={(e) => setLocalKioskName(e.target.value)}
              placeholder="e.g., Front Desk 1"
              className="w-full mt-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label
              htmlFor="kiosk-location-input"
              className="text-xs font-medium text-gray-600"
            >
              Location (optional)
            </label>
            <input
              id="kiosk-location-input"
              type="text"
              value={localKioskLocation}
              onChange={(e) => setLocalKioskLocation(e.target.value)}
              placeholder="e.g., NYC - Manhattan"
              className="w-full mt-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <button
            type="button"
            onClick={handleSaveIdentity}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors text-sm"
          >
            Save Identity
          </button>
        </div>
      </div>

      {/* Machine Context */}
      {onContextChange && (
        <div className="space-y-3 pb-6 border-b border-gray-200">
          <div>
            <h4 className="text-sm font-medium text-gray-700">
              Machine Context
            </h4>
            <p className="text-xs text-gray-500 mt-1">
              Initial values normally set from URL parameters
            </p>
          </div>

          <div className="space-y-3">
            {/* Org ID */}
            <div>
              <label
                htmlFor="org-id-input"
                className="text-xs font-medium text-gray-600"
              >
                Organization ID
              </label>
              <input
                id="org-id-input"
                type="number"
                value={localOrgId}
                onChange={(e) => setLocalOrgId(e.target.value)}
                className="w-full mt-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Practice Selection */}
            <div>
              <label
                htmlFor="practice-select"
                className="text-xs font-medium text-gray-600"
              >
                Practice
              </label>
              <select
                id="practice-select"
                value={localPracticeId}
                onChange={(e) => {
                  const selectedPractice = practiceData?.find(
                    (p) => String(p.practiceId) === e.target.value,
                  );
                  setLocalPracticeId(e.target.value);
                  setLocalEhrLocationId(selectedPractice?.ehrLocationId || "");
                }}
                className="w-full mt-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {practiceData?.map((practice) => (
                  <option
                    key={practice.practiceId}
                    value={String(practice.practiceId)}
                  >
                    {practice.locationName} (ID: {practice.practiceId}
                    {practice.ehrLocationId &&
                      `, EHR: ${practice.ehrLocationId}`}
                    )
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Practice ID: {localPracticeId}
                {localEhrLocationId &&
                  ` | EHR Location ID: ${localEhrLocationId}`}
              </p>
            </div>

            {/* Org Name */}
            <div>
              <label
                htmlFor="org-name-input"
                className="text-xs font-medium text-gray-600"
              >
                Organization Name
              </label>
              <input
                id="org-name-input"
                type="text"
                value={localOrgName}
                onChange={(e) => setLocalOrgName(e.target.value)}
                className="w-full mt-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* QR Mode */}
            <div>
              <label className="flex items-center gap-2 text-xs font-medium text-gray-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={localQrMode}
                  onChange={(e) => setLocalQrMode(e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                QR Code Mode
              </label>
              <p className="text-xs text-gray-500 mt-1 ml-6">
                Enable for appointment-based check-ins
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={handleApplyMachineContext}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
          >
            Apply & Persist Changes
          </button>

          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
            <p className="text-xs text-green-800">
              ✓ Settings are persisted to localStorage and will survive page
              refreshes and browser restarts
            </p>
          </div>
        </div>
      )}

      {/* Inactivity Timeout */}
      <div className="space-y-3">
        <div>
          {/* biome-ignore lint/a11y/noLabelWithoutControl: <explanation> */}
          <label className="text-sm font-medium text-gray-700">
            Inactivity Timeout
          </label>
          <p className="text-xs text-gray-500 mt-1">
            Time before showing "Are you still here?" dialog
          </p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="text-sm font-medium text-blue-900">
            Current: {formatTime(inactivityTimeout)}
          </div>
        </div>

        <div className="space-y-2">
          <p className="text-xs font-medium text-gray-600">Quick Presets:</p>
          <div className="grid grid-cols-2 gap-2">
            {presetOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => handleTimeoutChange(option.value)}
                className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                  inactivityTimeout === option.value
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-white text-gray-700 border-gray-300 hover:border-blue-300 hover:bg-blue-50"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
          <p className="text-xs text-green-800">
            ✓ Timeout setting is automatically persisted to localStorage
          </p>
        </div>

        <div className="pt-3 border-t border-gray-200">
          <p className="text-xs text-gray-500">
            💡 Tip: Use shorter timeouts (30s - 1m) for testing the inactivity
            dialog in development
          </p>
        </div>
      </div>

      {/* Behavior Settings */}
      <div className="space-y-3 pt-6 border-t border-gray-200">
        <div>
          <h4 className="text-sm font-medium text-gray-700">
            Behavior Settings
          </h4>
          <p className="text-xs text-gray-500 mt-1">
            Control navigation and camera permission behavior
          </p>
        </div>

        {/* Navigation Mode */}
        <div>
          <label
            htmlFor="navigation-mode"
            className="text-xs font-medium text-gray-600"
          >
            Reset Navigation Mode
          </label>
          <select
            id="navigation-mode"
            value={navigationMode}
            onChange={(e) => {
              const newMode = e.target.value;
              setNavigationMode(newMode);
              localStorage.setItem("kiosk_navigation_mode", newMode);
            }}
            className="w-full mt-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="stateMachine">
              State Machine (Preserve Camera Permission)
            </option>
            <option value="window">Window Reload (Legacy)</option>
          </select>
          <p className="text-xs text-gray-500 mt-1">
            {navigationMode === "stateMachine"
              ? "✓ No page reload - camera permission preserved"
              : "⚠ Page reload - camera permission may be re-requested"}
          </p>
        </div>

        {/* Permission Request Mode */}
        <div>
          <label
            htmlFor="permission-mode"
            className="text-xs font-medium text-gray-600"
          >
            Camera Permission Request
          </label>
          <select
            id="permission-mode"
            value={permissionRequestMode}
            onChange={(e) => {
              const newMode = e.target.value;
              setPermissionRequestMode(newMode);
              localStorage.setItem("kiosk_permission_request_mode", newMode);
            }}
            className="w-full mt-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="startup">On Startup (Immediate)</option>
            <option value="onDemand">On Demand (When Needed)</option>
          </select>
          <p className="text-xs text-gray-500 mt-1">
            {permissionRequestMode === "startup"
              ? "✓ Request on page load - ready when needed"
              : "⏳ Request when photo capture starts - delayed prompt"}
          </p>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
          <p className="text-xs text-amber-800">
            ⚠ Changes require page reload to take effect
          </p>
        </div>

        <button
          type="button"
          onClick={() => window.location.reload()}
          className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
        >
          Reload Page to Apply
        </button>
      </div>
    </div>
  );
}
